import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EXAM_PASS_SCORE } from '@proref/schemas';
import { Icon } from '../components/shared/Icons';
import { Skeleton } from '../components/shared/Skeleton';
import { apiErrorMessage } from '../lib/api';
import { ExamResultItemCard } from '../components/exams/ExamResultItemCard';
import { useExamResultsQuery } from '../hooks/use-exams';
import { useReleaseBodyPointerLock } from '../hooks/use-release-body-pointer-lock';

// Shared with the API so the admin's "passed" column and this verdict can never disagree.
const PASS_PERCENT = EXAM_PASS_SCORE * 100;

function pct(score: number): number {
  // One decimal, not a whole number: on a 30-point exam a single point is 3.3%, and rounding it
  // away can put a 84.6% result on the wrong side of the pass line in the display.
  return Math.round(score * 1000) / 10;
}
function tone(p: number): string {
  return p >= PASS_PERCENT ? 'text-accent' : 'text-danger';
}

// Option letters, matching the runner and the admin editor.
export function ExamResultsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: results, isLoading, isError, error } = useExamResultsQuery(id);
  // Finishing an exam navigates here straight out of the runner Dialog; release any body
  // pointer-events lock Radix left behind so this page is actually clickable (see the hook).
  useReleaseBodyPointerLock();

  if (isLoading) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError || !results) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('exams.results.couldNotLoad'))}
      </div>
    );
  }

  const p = pct(results.score);
  const passed = p >= PASS_PERCENT;

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-2">
        <Link to="/rule-tests" className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-ink">
          <Icon.chevronLeft className="h-3.5 w-3.5" /> {t('exams.results.backToExams')}
        </Link>
      </div>

      {/* Score header — percentage, the pass verdict, and the raw point totals behind it. */}
      <div className="mb-6 rounded-lg border border-line bg-surface p-6">
        <div className="flex items-center gap-5">
          <div className={'font-display text-[52px] leading-none ' + tone(p)}>{p}%</div>
          <div>
            <div className={'text-[15px] font-semibold ' + (passed ? 'text-accent' : 'text-danger')}>
              {passed ? t('exams.results.passed') : t('exams.results.notPassed')}
            </div>
            <p className="text-[12.5px] text-muted">
              {passed
                ? t('exams.results.abovePassMark', { pct: PASS_PERCENT })
                : t('exams.results.belowPassMark', { pct: PASS_PERCENT })}
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-4 border-t border-line pt-4 text-center">
          <div>
            <div className="font-display text-[22px] leading-none text-ink">{results.earned_points}</div>
            <div className="mt-1 text-[11.5px] text-muted">{t('exams.results.points')}</div>
          </div>
          <div>
            <div className="font-display text-[22px] leading-none text-ink-2">{results.max_points}</div>
            <div className="mt-1 text-[11.5px] text-muted">{t('exams.results.maximum')}</div>
          </div>
          <div>
            <div className="font-display text-[22px] leading-none text-ink-2">
              {results.details_locked ? results.total : `${results.correct}/${results.total}`}
            </div>
            <div className="mt-1 text-[11.5px] text-muted">
              {/* While locked the per-question tally is withheld along with the answers, so the
                  tile falls back to the question count rather than showing a misleading 0. */}
              {results.details_locked ? t('exams.results.questions') : t('exams.results.questionsFullyCorrect')}
            </div>
          </div>
        </div>
      </div>

      {results.details_locked ? (
        // The answer key genuinely isn't in this payload — the server withholds it until the
        // admin releases, so there is nothing to reveal here even in the devtools.
        <div className="rounded-lg border border-warn/40 bg-warn/[0.07] p-6 text-center">
          <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-warn/20 text-warn">
            <Icon.lock className="h-5 w-5" />
          </div>
          <div className="text-[14px] font-semibold text-ink">{t('exams.results.answersNotReleasedTitle')}</div>
          <p className="mx-auto mt-1 max-w-md text-[12.5px] leading-relaxed text-muted">
            {t('exams.results.answersNotReleasedBody')}
          </p>
        </div>
      ) : (
        <>
          <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted">
            {t('exams.results.review')}
          </h2>
          <div className="flex flex-col gap-3">
            {results.items.map((item, i) => (
              <ExamResultItemCard key={item.exam_question_id} item={item} index={i} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
